import { inject, Injectable, NgZone, Renderer2 } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { DomSanitizer } from '@angular/platform-browser';
import { Data, Router } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { LoadingService } from '@core/loading.service';
import { UtilityService } from '@core/utility.service';
import { catchError, map, of } from 'rxjs';
import { LoginDataResolverService } from 'src/app/services/login-data-resolver.service';
import { PaymentCardService } from 'src/app/services/payment-card.service';
import { PaymentService } from 'src/app/services/payment.service';
import { environment } from 'src/environments/environment';
import type { PaymentPage } from '../payment.page';


declare var window;
@Injectable()
export class PaymilPaymentService {
    private router = inject(Router);
    private paymentCardService = inject(PaymentCardService);
    private paymentService = inject(PaymentService);
    private utilityService = inject(UtilityService);
    private renderer = inject(Renderer2);
    private loadingService = inject(LoadingService);
    private loginDataResolverService = inject(LoginDataResolverService);
    private authService = inject(AuthService);
    private ngZone = inject(NgZone);
    private sanitizer = inject(DomSanitizer);

    constructor(private angularFireAuth: AngularFireAuth) { }

    async addNewCard(purchaseDetail: Data, window: Data) {
        const currentUrl = this.router.url;
        localStorage.setItem("currentRoute", currentUrl);
        const oldProsub = localStorage.getItem("initialProsub");
        const prosub_id = localStorage.getItem("prosub_id");
        const checkoutId = "21";

        const cardDetails = this.paymentCardService?.setSelectedCard(purchaseDetail);

        if (oldProsub === prosub_id && cardDetails !== checkoutId && !purchaseDetail?.p_type) {
            const c_Id = localStorage.getItem("c_id");
            // Cached checkout must be a real id. Missing/null/"null" would load
            // paymentWidgets.js?checkoutId=null and show "checkoutId null is invalid".
            // Fall through to load-form so getCheckoutId fetches a fresh id.
            if (!c_Id || c_Id === "null" || c_Id === "undefined") {
                return { status: 'load-form' };
            }

            this.paymentService.currentNdcId = c_Id;

            try {
                await this.unloadPaymentWidget(window);
            } catch (error) {
                throw new Error(`Failed to unload payment widget: ${error}`);
            }

            const pathString = this.utilityService.loadScriptForPayment([
                environment.paymentWidgetUrl + `${c_Id}`
            ]);
            this.dismissLoaderAfterTimeout();
            this.utilityService.checkIframeInputFocus();

            localStorage.removeItem("minutes");
            localStorage.removeItem("c_id");
            localStorage.removeItem("initialProsub");

            return {
                status: 'new-card',
                cardDetails,
                currentNdc: c_Id,
                pathString,
                insertCustomerPurchaseRes: this.loginDataResolverService.insertCustomerPurchaseRes,
                currentUrl
            };
        }

        return { status: 'load-form' };
    }

    manualLoginPayment(token: string) {
        return this.authService.getV2CloudFunction("tokenvalidater", { token }).pipe(
            map(async (res) => {
                const { status, token, token_refresh } = res;
                if (!status || !token) return;

                if (token_refresh) {
                    localStorage.setItem("fbToken", token);
                }
                await this.angularFireAuth.signInWithCustomToken(token);
                return await this.angularFireAuth.currentUser;
            })
        )
    }

    afterPaymentDone(currentNdc: string) {
        return this.paymentService.getCustomerPurchaseDetailsFromSQL(currentNdc).pipe(
            map((data: Data) => {
                const purchaseDoc = data;
                return purchaseDoc;
            }),
            catchError((err) => of(err))
        )
    }

    getCheckoutId(purchaseDetail: Data, data: {
        userId
        tp1
        tp2
        tp3
        tId
        currency,
        payReason
    }) {
        const { userId, tp1, tp2, tp3, tId, currency, payReason } = data
        const other = localStorage.getItem("Other");
        let cp_request: Data = {
            user_id: userId ?? this.authService.userMSN$.value,
            prosub_id: purchaseDetail?.prosub_id ?? "",
            TP1: tp1,
            TP2: tp2,
            TP3: tp3,
            T_ID: tId,
            offer_id: purchaseDetail?.offers?.offer_id ?? "",
            value: purchaseDetail?.offers?.price ?? "",
            preferred_currency: {
                Value: purchaseDetail?.offers?.price ?? "",
                currency: purchaseDetail?.offers?.currency ? purchaseDetail?.offers?.currency : "GBP",
            },
            p_type: purchaseDetail?.p_type ?? "10",
            product_description: purchaseDetail?.product_description ?? "",
            other: other ?? "",
        };
        if (cp_request.value === 0) {
            cp_request = { ...cp_request, payment_type: "PA", sub_status: "pre_auth" };
        }
        this.paymentService.preferredCurrencyObj = {
            amount: String(
                purchaseDetail?.offers?.int_price?.[currency]
                    ? purchaseDetail?.offers?.int_price?.[currency]?.toFixed(2)
                    : purchaseDetail?.prize && !isNaN(purchaseDetail?.prize)
                        ? parseFloat(purchaseDetail?.prize)?.toFixed(2)
                        : 0
            ),
            currency: currency,
        };

        return this.paymentService.getCheckoutId(
            parseFloat(purchaseDetail.prize)?.toFixed(2),
            purchaseDetail?.offers?.currency ? purchaseDetail?.offers?.currency : "GBP",
            cp_request,
            payReason
        )
    }



    //Helpers

    async unloadPaymentWidget(window): Promise<void> {
        try {
            if (window?.wpwl !== undefined && window?.wpwl?.unload !== undefined) {
                await window.wpwl.unload();

                if (document && document !== null && typeof document !== "undefined") {
                    const scripts = document?.querySelectorAll('script[src*="static.min.js"]');
                    scripts?.forEach((script) => {
                        this.renderer.removeChild(document?.body, script);
                    });
                }
            }
        } catch (error) {
            return error;
        }
    }

    // To dismiss the loading animation once the payment widget is loaded
    dismissLoaderAfterTimeout(attempt = 0): void {
        const maxAttempts = 3;
        const delay = Math.pow(2, attempt) * 1000;

        if (attempt === 0) {
            this.loadingService.dismiss();
        }

        if (attempt < maxAttempts) {
            setTimeout(() => {
                this.dismissLoaderAfterTimeout(attempt + 1);
            }, delay);
        }
    }


    /**
     * Wait for payment widget iframe communication objects to be fully initialized
     * This prevents the race condition where users click "Pay now" before iframes are ready
     * CRITICAL FIX: Now checks for OPPWA widget's internal iframe communication channels
     * to prevent "fe.iframeCommunications['cvv_'+n].validateInput" errors
     * @returns Promise that resolves when widget is ready or rejects on timeout
     */
    public waitForPaymentWidgetReady(): Promise<void> {
        return new Promise((resolve, reject) => {
            const maxWaitTime = 15000; // 15 seconds timeout (increased for slower connections)
            const checkInterval = 200; // Check every 200ms (increased to reduce CPU usage)
            const startTime = Date.now();
            let lastCheckDescription = "";

            const checkIframesReady = () => {
                try {
                    // Access the payment widget's internal objects
                    const windowObj = window as Data;

                    // STEP 1: Check if the payment widget script is loaded
                    const paymentWidgetLoaded = windowObj.wpwl !== undefined;

                    if (!paymentWidgetLoaded) {
                        lastCheckDescription = "Payment widget (wpwl) not loaded yet";
                        // Widget not loaded yet, keep checking
                        if (Date.now() - startTime < maxWaitTime) {
                            setTimeout(checkIframesReady, checkInterval);
                        } else {
                            reject(new Error(`Payment widget load timeout: ${lastCheckDescription}`));
                        }
                        return;
                    }

                    // STEP 2: Check for critical iframe elements in the DOM
                    const cardNumberIframe = document.querySelector(".wpwl-control-cardNumber");
                    const cvvIframe = document.querySelector(".wpwl-control-cvv");
                    const expiryIframe = document.querySelector(".wpwl-control-expiry");

                    // Verify all critical iframes are present in the DOM
                    const iframesPresent = cardNumberIframe && cvvIframe && expiryIframe;

                    if (!iframesPresent) {
                        lastCheckDescription = "Payment iframes not rendered in DOM yet";
                        // Iframes not rendered yet, keep checking
                        if (Date.now() - startTime < maxWaitTime) {
                            setTimeout(checkIframesReady, checkInterval);
                        } else {
                            reject(new Error(`Payment iframes not rendered within timeout: ${lastCheckDescription}`));
                        }
                        return;
                    }

                    // STEP 3: Check for the payment form element
                    const formElement = document.querySelector(".wpwl-form-card");

                    if (!formElement) {
                        lastCheckDescription = "Payment form element not ready";
                        // Keep checking
                        if (Date.now() - startTime < maxWaitTime) {
                            setTimeout(checkIframesReady, checkInterval);
                        } else {
                            reject(new Error(`Payment form not ready within timeout: ${lastCheckDescription}`));
                        }
                        return;
                    }

                    // STEP 4: CRITICAL - Verify the widget's internal iframe communication objects are initialized
                    // This is the KEY FIX for the "fe.iframeCommunications['cvv_'+n].validateInput" error
                    // The OPPWA widget needs time to set up its internal communication channels after iframes load

                    // Give the widget additional time to initialize internal communication channels
                    // Even though DOM elements exist, the widget's JavaScript needs to complete initialization
                    const elapsedTime = Date.now() - startTime;
                    const minInitTime = 1000; // Minimum 1 second for widget to initialize internal state

                    if (elapsedTime < minInitTime) {
                        lastCheckDescription = `Widget initializing internal state (${elapsedTime}ms < ${minInitTime}ms)`;
                        setTimeout(checkIframesReady, checkInterval);
                        return;
                    }

                    // STEP 5: Additional validation - check if the payment button exists and is interactable
                    const payButton = document.querySelector(".wpwl-button-pay") as HTMLButtonElement;

                    if (!payButton) {
                        lastCheckDescription = "Payment button not found";
                        if (Date.now() - startTime < maxWaitTime) {
                            setTimeout(checkIframesReady, checkInterval);
                        } else {
                            reject(new Error(`Payment button not found within timeout: ${lastCheckDescription}`));
                        }
                        return;
                    }

                    // STEP 6: Final validation - ensure iframes are not in a disabled state
                    const cardNumberDisabled = cardNumberIframe?.hasAttribute('disabled');
                    const cvvDisabled = cvvIframe?.hasAttribute('disabled');

                    if (cardNumberDisabled || cvvDisabled) {
                        lastCheckDescription = "Iframes still in disabled state";
                        if (Date.now() - startTime < maxWaitTime) {
                            setTimeout(checkIframesReady, checkInterval);
                        } else {
                            // Don't reject if disabled - just log and resolve
                            this.authService?.errorHandlerLogs?.(`Warning: ${lastCheckDescription}, but proceeding anyway`);
                            resolve();
                        }
                        return;
                    }

                    // All checks passed - widget is fully initialized
                    this.authService?.errorHandlerLogs?.(`Payment widget ready after ${elapsedTime}ms`);
                    resolve();

                } catch (error) {
                    // If any error occurs during checks, keep trying until timeout
                    lastCheckDescription = `Error during widget check: ${error.message || error}`;
                    if (Date.now() - startTime < maxWaitTime) {
                        setTimeout(checkIframesReady, checkInterval);
                    } else {
                        reject(new Error(`Payment widget initialization failed: ${lastCheckDescription}`));
                    }
                }
            };

            // Start checking
            checkIframesReady();
        });
    }

    removePaymentHeaderFromIframe() {
        // Add null-safety checks to prevent errors when wpwlOptions is not yet initialized
        if (typeof window === 'undefined') {
            return;
        }

        this.ngZone.run(() => {
            try {
                // Ensure wpwlOptions exists before attempting to set onReady callback
                if (!window.wpwlOptions) {
                    window.wpwlOptions = {};
                }

                (window as Data).wpwlOptions.onReady = () => {
                    const button: HTMLButtonElement = document.querySelector(".wpwl-button-pay");
                    if (button) {
                        // Disable button initially until iframes are ready
                        button.disabled = true;
                        button.style.opacity = "0.5";
                        button.style.cursor = "not-allowed";

                        // Wait for payment widget iframes to be fully initialized
                        // This is CRITICAL to prevent "fe.iframeCommunications['cvv_'+n].validateInput" errors
                        this.waitForPaymentWidgetReady()
                            .then(() => {
                                // Enable button only after iframes are ready
                                button.disabled = false;
                                button.style.opacity = "1";
                                button.style.cursor = "pointer";
                                this.authService?.errorHandlerLogs?.("Payment widget fully initialized - button enabled");
                            })
                            .catch((error) => {
                                // Log the error with details for debugging
                                this.authService?.errorHandlerLogs?.(
                                    "Payment widget initialization timeout - enabling button anyway",
                                    error
                                );

                                // Enable button anyway after timeout to prevent blocking users
                                // The widget's internal validation will catch any real issues
                                button.disabled = false;
                                button.style.opacity = "1";
                                button.style.cursor = "pointer";
                            });

                        // Note: Cross-origin iframe manipulation removed to prevent SecurityError
                        // Payment processor iframes are served from different domains and cannot be accessed
                        // due to browser security policies (Same-Origin Policy)

                    }
                };
            } catch (error) {
                // Silently handle any errors during wpwlOptions initialization
                // This prevents breaking the page if the payment widget is not available
                this.authService?.errorHandlerLogs?.("Error initializing payment widget options:", error);
            }
        });
    }

    public afterNewCardPaymentDoneCheckout(ctx: PaymentPage): void {
        try {
            if (!ctx.paymentReceived && localStorage.getItem('d')) {
                let urlData: { id?: string; resourcePath?: string } | null = null;
                try {
                    urlData = JSON.parse(localStorage.getItem('d'));
                } catch {
                    this.authService.errorHandlerLogs('afterNewCardPaymentDoneCheckout: failed to parse localStorage d');
                    return;
                }
                if (!urlData?.id) return;
                ctx.paymentReceived = true;
                localStorage.removeItem('d');
                setTimeout(() => {
                    if (!ctx.userId) return;
                    const pathString = ctx.pathString ? String(ctx.pathString) : null;
                    if (!pathString) {
                        this.authService.errorHandlerLogs('afterNewCardPaymentDoneCheckout: pathString is missing');
                        return;
                    }
                    const path_id = 'At4Vyy58QQ2vknYAhw7T';
                    const dv = { docId: pathString };
                    const method = 'update';
                    this.authService
                        .getV2CloudFunction('updatefirestoredoc', {
                            path_id,
                            doc_data: { payment_response_checkout: { id: urlData.id, resource_url: urlData.resourcePath, checkout_done_at: new Date() } },
                            dv, method,
                        })
                        .subscribe({ error: (err) => this.authService.errorHandlerLogs('Error in afterNewCardPaymentDoneCheckout:', err) });

                    this.paymentService.checkPaymentStatus(urlData.id, ctx.paymentMode)
                        .subscribe({
                            next: (payment_status) => {
                                this.authService
                                    .getV2CloudFunction('updatefirestoredoc', { path_id, doc_data: { payment_response: payment_status }, dv, method })
                                    .subscribe({
                                        next: () => {
                                            ctx.payment_status = payment_status;
                                            const description = String(payment_status?.result?.description ?? '').toLowerCase();
                                            const isSuccess = description.includes('successfully') || description.includes('succeeded');
                                            const toastCode = this.paymentService.mapPaymentToastCode(payment_status);
                                            const fallback = this.paymentService.mapPaymentToastFallback(payment_status);
                                            ctx.toastFacade.showByCode(toastCode, fallback, { journey: 'payment', trigger: 'manual_login_payment_result', reason_code: this.paymentService.mapPaymentReasonCode(payment_status) });
                                            if (isSuccess) {
                                                ctx.isPaymentDone = false;
                                                const token = localStorage.getItem('fbToken');
                                                if (token) ctx.manualLoginPayment(token, payment_status);
                                            } else {
                                                ctx.isPaymentDone = false;
                                                ctx.redirectlink = this.sanitizer.bypassSecurityTrustResourceUrl(environment.checkOutUrl);
                                            }
                                        },
                                        error: (err) => this.authService.errorHandlerLogs('Error updating card:', err),
                                    });
                            },
                            error: (err) => this.authService.errorHandlerLogs('checkPaymentStatus error in afterNewCardPaymentDoneCheckout:', err),
                        });
                }, 1000);
            }
        } catch (err) {
            this.authService.errorHandlerLogs('afterNewCardPaymentDoneCheckout unexpected error:', err);
        }
    }
}
